// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-5 §2.5 — the OwlRx Plan of Treatment dashboard's lists. Same columns, buttons and ⋯ menu
// items as POT classic (PotDashboard.tsx), on the kit: StatusChip badges, ActionMenu, AppButton,
// EmptyState, `--color-*` tokens. CSS-grid rows with table roles (the kit has no list wrapper yet
// and a raw <table> is ratcheted).
import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import ActionMenu, { type MenuAction } from '../../components/ui/ActionMenu'
import AppButton from '../../components/ui/AppButton'
import EmptyState from '../../components/ui/EmptyState'
import PatientLanguageBadge from '../../components/ui/PatientLanguageBadge'
import StatusChip from '../../components/ui/StatusChip'
import { formatUsDateTime } from '../../api/format'
import type { PotWorklistRow } from '../../api/carerxPot'
import { QUEUE_STAGE, SIG_BADGE, isClassicRow, lastTouched, minutesOf, sigDateLine, type PotRow } from './carerxPotDashboardLogic'

// ── grid "table" primitives ──
const TH = { fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--color-text-soft)', padding: '6px 8px', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' } as const
const TD = { padding: '7px 8px', borderBottom: '1px solid var(--color-border)', fontSize: 12.5, color: 'var(--color-text)', display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 } as const
const META = { fontSize: 11, color: 'var(--color-text-soft)' } as const

function Grid({ cols, label, children }: { cols: string; label: string; children: ReactNode }) {
  return <Box role="table" aria-label={label} sx={{ width: '100%', overflowX: 'auto' }}><Box sx={{ minWidth: 720, display: 'grid', gridTemplateColumns: cols }}>{children}</Box></Box>
}
const Th = ({ children }: { children?: ReactNode }) => <Box role="columnheader" sx={TH}>{children}</Box>
const Td = ({ children, selected, row }: { children?: ReactNode; selected?: boolean; row?: boolean }) =>
  <Box role="cell" sx={{ ...TD, ...(row ? { flexDirection: 'row', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' } : {}), ...(selected ? { bgcolor: 'var(--color-primary-soft)' } : {}) }}>{children}</Box>
const RowBox = ({ children, voided }: { children: ReactNode; voided?: boolean }) => <Box role="row" sx={{ display: 'contents', opacity: voided ? 0.6 : 1 }}>{children}</Box>
const EmptyRow = ({ title, hint }: { title: string; hint?: string }) =>
  <Box role="row" sx={{ gridColumn: '1 / -1' }}><Box role="cell"><EmptyState title={title} hint={hint} /></Box></Box>
const Check = ({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) =>
  <input type="checkbox" aria-label={label} checked={checked} onChange={e => onChange(e.target.checked)} style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--color-primary)' }} />

// ── cells ──
/** D-POT-5 P1: a row authored in POT classic, visible while the two instruments are compared. */
const ClassicChip = () => <Box component="span" title="Authored in POT classic (pot_classic_v1)" sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', borderRadius: 1, px: 0.6, py: 0.1, ml: 0.75, bgcolor: 'var(--color-surface-alt)', color: 'var(--color-text-soft)', border: '1px solid var(--color-border)' }}>classic</Box>
const PatientCell = ({ r, selected, voided }: { r: PotRow; selected?: boolean; voided?: boolean }) => (
  <Td selected={selected}>
    <Box component="span" sx={{ fontWeight: 700, textDecoration: voided ? 'line-through' : 'none' }}>{r.patient_name}<PatientLanguageBadge patientId={r.patient_id} />{isClassicRow(r) && <ClassicChip />}</Box>
    {r.mrn && <Box sx={META}>MRN: {r.mrn}</Box>}
  </Td>
)
const DrugCell = ({ r, selected }: { r: PotRow; selected?: boolean }) => <Td selected={selected}>{r.drug_name ?? '—'}{r.diagnosis && <Box sx={META}>{r.diagnosis}</Box>}</Td>
const WhenCell = ({ iso, selected, missing }: { iso: string | null | undefined; selected?: boolean; missing?: string }) =>
  <Td selected={selected}>{iso ? formatUsDateTime(iso) : <Box component="span" sx={META}>{missing ?? '—'}</Box>}</Td>

export function SigBadge({ r }: { r: PotRow }) {
  const s = SIG_BADGE[r.signature] ?? SIG_BADGE.not_sent
  const line = sigDateLine(r)
  return <Box><StatusChip status={r.signature} label={s.label} tone={s.tone} />{line && <Box sx={{ ...META, mt: 0.4 }}>{line}</Box>}{r.signature === 'voided' && r.void_reason && <Box sx={{ ...META, fontStyle: 'italic' }}>{r.void_reason}</Box>}</Box>
}

const MoreMenu = ({ actions }: { actions: MenuAction[] }) => <ActionMenu label="More actions" actions={actions} />

// ── Queue (D-POT-7): ready to start · unclaimed · assigned / claimed but not started ──
const CONSENT_TONE = { complete: 'green', pending: 'amber', none: 'amber' } as const
export function QueueTable({ rows, total, dateCell, actionsFor }: {
  rows: PotWorklistRow[]; total: number
  /** The visit date, or the schedule control when the visit has none yet. */
  dateCell: (r: PotWorklistRow) => ReactNode
  actionsFor: (r: PotWorklistRow) => ReactNode
}) {
  return (
    <Grid label="Queue" cols="1.1fr 1.2fr 2fr 1.8fr 1fr 1.4fr 2fr">
      <Th>Stage</Th><Th>Visit date</Th><Th>Patient</Th><Th>Drug / Dx</Th><Th>Consent</Th><Th>Nurse</Th><Th>Actions</Th>
      {rows.length === 0 && <EmptyRow title={total > 0 ? 'Nothing in the queue matches the current filters.' : 'The queue is empty.'} hint={total > 0 ? undefined : 'A patient appears here once they pass enrollment and sign consent, or when a visit is started by hand.'} />}
      {rows.map(r => {
        const st = r.stage ? QUEUE_STAGE[r.stage] : null
        return (
          <RowBox key={r.visit_id ?? `ready-${r.therapy_id}`}>
            <Td>{st && <StatusChip status={r.stage ?? ''} label={st.label} tone={st.tone} />}</Td>
            <Td>{r.kind === 'ready' ? <Box component="span" sx={META}>Not started</Box> : dateCell(r)}{r.visit_type && <Box sx={META}>{r.visit_type}</Box>}</Td>
            <Td>
              <Box component="span" sx={{ fontWeight: 700 }}>{r.patient_name}<PatientLanguageBadge patientId={r.patient_id} /></Box>
              {r.mrn && <Box sx={META}>MRN: {r.mrn}</Box>}
            </Td>
            <Td>{r.drug_name ?? '—'}{r.diagnosis && <Box sx={META}>{r.diagnosis}</Box>}{r.prescriber && <Box sx={META}>{r.prescriber}</Box>}</Td>
            <Td>{r.consent_status && <StatusChip status={r.consent_status} label={r.consent_status === 'complete' ? 'Signed' : r.consent_status === 'pending' ? 'Pending' : 'None'} tone={CONSENT_TONE[r.consent_status]} />}</Td>
            <Td>{r.nurse_name ?? '—'}{r.agency_name && <Box sx={META}>{r.agency_name}</Box>}</Td>
            <Td row>{actionsFor(r)}</Td>
          </RowBox>
        )
      })}
    </Grid>
  )
}

// ── In progress ──
export interface SelectionProps { selected: Set<string>; onToggle: (id: string, on: boolean) => void; onToggleAll: (on: boolean) => void }
const allSelected = (rows: PotRow[], set: Set<string>) => rows.length > 0 && rows.every(r => set.has(r.visit_id))

export function InProgressTable({ rows, total, sel, onResume, menuFor }: { rows: PotRow[]; total: number; sel: SelectionProps; onResume: (r: PotRow) => void; menuFor: (r: PotRow) => MenuAction[] }) {
  return (
    <Grid label="In progress" cols="40px 2fr 1.4fr 2fr 1.3fr 1.5fr">
      <Th><Check label="Select all drafts" checked={allSelected(rows, sel.selected)} onChange={sel.onToggleAll} /></Th>
      <Th>Patient</Th><Th>Nurse</Th><Th>Drug / Dx</Th><Th>Last updated</Th><Th>Actions</Th>
      {rows.length === 0 && <EmptyRow title={total > 0 ? 'No drafts match the current filters.' : 'No drafts in progress.'} hint={total > 0 ? undefined : 'Start a Plan of Treatment from "New Assessment" or claim one from the pool.'} />}
      {rows.map(r => {
        const on = sel.selected.has(r.visit_id)
        return (
          <RowBox key={r.visit_id}>
            <Td selected={on}><Check label={`Select ${r.patient_name}`} checked={on} onChange={v => sel.onToggle(r.visit_id, v)} /></Td>
            <PatientCell r={r} selected={on} />
            <Td selected={on}>{r.nurse_name ?? '—'}</Td>
            <DrugCell r={r} selected={on} />
            <WhenCell iso={lastTouched(r)} selected={on} />
            <Td selected={on} row><AppButton variant="contained" size="small" onClick={() => onResume(r)}>Resume</AppButton><MoreMenu actions={menuFor(r)} /></Td>
          </RowBox>
        )
      })}
    </Grid>
  )
}

// ── Completed ──
export function CompletedTable({ rows, total, sel, buttonsFor, menuFor }: { rows: PotRow[]; total: number; sel: SelectionProps; buttonsFor: (r: PotRow) => ReactNode; menuFor: (r: PotRow) => MenuAction[] }) {
  return (
    <Grid label="Completed assessments" cols="40px 2fr 1.2fr 1.8fr 1.2fr 1.5fr 2fr">
      <Th><Check label="Select all assessments" checked={allSelected(rows, sel.selected)} onChange={sel.onToggleAll} /></Th>
      <Th>Patient</Th><Th>Nurse</Th><Th>Drug / Dx</Th><Th>Completed</Th><Th>Signature</Th><Th>Actions</Th>
      {rows.length === 0 && <EmptyRow title={total > 0 ? 'No completed assessments match the current filters.' : 'No completed assessments yet.'} />}
      {rows.map(r => {
        const on = sel.selected.has(r.visit_id)
        const min = minutesOf(r.worked_seconds)
        return (
          <RowBox key={r.visit_id}>
            <Td selected={on}><Check label={`Select ${r.patient_name}`} checked={on} onChange={v => sel.onToggle(r.visit_id, v)} /></Td>
            <PatientCell r={r} selected={on} />
            <Td selected={on}>{r.nurse_name ?? '—'}{min != null && <Box sx={META}>{min} min</Box>}</Td>
            <DrugCell r={r} selected={on} />
            <WhenCell iso={r.completed_at} selected={on} />
            <Td selected={on}><SigBadge r={r} /></Td>
            <Td selected={on} row>{buttonsFor(r)}<MoreMenu actions={menuFor(r)} /></Td>
          </RowBox>
        )
      })}
    </Grid>
  )
}

// ── Voided (accounts that can void) ──
export function VoidedTable({ rows, total, onWorkingPdf, onView }: { rows: PotRow[]; total: number; onWorkingPdf: (r: PotRow) => void; onView: (r: PotRow) => void }) {
  return (
    <Grid label="Voided assessments" cols="2fr 1.2fr 1.8fr 1.2fr 1.8fr 1.6fr">
      <Th>Patient</Th><Th>Nurse</Th><Th>Drug / Dx</Th><Th>Completed</Th><Th>Signature</Th><Th>Actions</Th>
      {rows.length === 0 && <EmptyRow title={total > 0 ? 'No voided assessments match the current filters.' : 'No voided assessments.'} />}
      {rows.map(r => (
        <RowBox key={r.visit_id} voided>
          <PatientCell r={r} voided />
          <Td>{r.nurse_name ?? '—'}</Td>
          <DrugCell r={r} />
          <WhenCell iso={r.completed_at ?? r.created_at} />
          <Td><SigBadge r={{ ...r, signature: 'voided' }} /></Td>
          <Td row>
            {r.document_id && <AppButton variant="outlined" size="small" onClick={() => onWorkingPdf(r)}>Working PDF</AppButton>}
            {r.form_status !== 'none' && <MoreMenu actions={[{ key: 'view', label: 'View', onClick: () => onView(r) }]} />}
          </Td>
        </RowBox>
      ))}
    </Grid>
  )
}
